/**
 * SpotifyPlayer — slim orchestrator for the persistent music player.
 *
 * The pre-Feb-2026 version was a 574-line monolith owning state, side-effects,
 * and 6 distinct UI surfaces. It now delegates to:
 *
 *   - `useSpotifyPlayerState`  (hook) — all local UI state + side-effects
 *   - `<SpotifyPill>`           collapsed launcher (when `pillOpen=false`)
 *   - `<SpotifyHeader>`         eyebrow row + icon buttons
 *   - `<SpotifyNotConnected>` / `<SpotifyPremiumGate>` — gates
 *   - `<SpotifyNowPlaying>`     artwork + transport + seek + volume
 *   - `<SpotifySearchPanel>`    inline expandable search
 *   - `<SpotifyBrowsePanel>`    tabs + items + default-URL editor
 *
 * Behaviour is byte-for-byte identical to the original — verified by the
 * existing testing-agent suite + manual smoke after the split.
 */
import React from "react";
import { useAuth } from "../context/AuthContext";
import { useMusic } from "../context/MusicContext";

import SpotifyPill from "./spotify/SpotifyPill";
import SpotifyHeader from "./spotify/SpotifyHeader";
import { SpotifyNotConnected, SpotifyPremiumGate } from "./spotify/SpotifyGates";
import SpotifyNowPlaying from "./spotify/SpotifyNowPlaying";
import SpotifySearchPanel from "./spotify/SpotifySearchPanel";
import SpotifyBrowsePanel from "./spotify/SpotifyBrowsePanel";
import { useSpotifyPlayerState } from "./spotify/useSpotifyPlayerState";

export default function SpotifyPlayer() {
  const { user, setUser } = useAuth();
  const {
    current, setCurrent, playItem,
    connect, status,
    pillOpen: open, setPillOpen: setOpen,
    sdkReady, sdkError, sdkState, sdk,
    accentStyle,
  } = useMusic();

  const state = useSpotifyPlayerState({
    status, sdk, sdkState, open, user, setUser, setCurrent,
  });

  if (!user) return null;

  // Collapsed pill — single tap to open
  if (!open) {
    const track = sdkState?.track_window?.current_track;
    const paused = sdkState?.paused !== false;
    return (
      <SpotifyPill
        track={track}
        paused={paused}
        accentStyle={accentStyle}
        onOpen={() => setOpen(true)}
      />
    );
  }

  // Expanded drawer
  const track = sdkState?.track_window?.current_track;
  const position = state.seekPos ?? sdkState?.position ?? 0;
  const duration = sdkState?.duration ?? track?.duration_ms ?? 0;
  const paused = sdkState?.paused !== false;
  const isPremium = status?.connected && status?.premium === true;

  return (
    <div
      className="fixed bottom-16 md:bottom-0 inset-x-0 z-40 text-white"
      style={accentStyle}
      data-testid="spotify-persistent-wrap"
    >
      <div className="relative">
        {/* Album-art glow underlay */}
        {track?.album?.images?.[0]?.url && (
          <>
            <div
              className="absolute inset-0 -z-10 bg-cover bg-center opacity-40 blur-3xl saturate-150 scale-150"
              style={{ backgroundImage: `url(${track.album.images[0].url})` }}
              aria-hidden
            />
            <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/85 via-black/70 to-black/40" aria-hidden />
          </>
        )}
        <div className="bg-black/40 backdrop-blur-2xl backdrop-saturate-150 border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 pt-3 pb-4">
            <SpotifyHeader
              status={status}
              isPremium={isPremium}
              onToggleSearch={() => state.setSearchOpen((v) => !v)}
              onClose={() => setOpen(false)}
            />

            {!status?.connected && <SpotifyNotConnected onConnect={connect} />}
            {status?.connected && !isPremium && <SpotifyPremiumGate />}

            {/* SDK init error */}
            {isPremium && sdkError && (
              <div
                className="rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/30 p-3 mb-3 text-xs text-[#FF3B30]"
                data-testid="spotify-sdk-error"
              >
                {sdkError}
              </div>
            )}

            {isPremium && (
              <SpotifyNowPlaying
                track={track}
                paused={paused}
                position={position}
                duration={duration}
                volume={state.volume}
                sdk={sdk}
                sdkReady={sdkReady}
                onSeek={state.onSeek}
                onVolume={state.onVolume}
              />
            )}

            <SpotifySearchPanel
              searchOpen={state.searchOpen}
              query={state.query}
              setQuery={state.setQuery}
              searchResults={state.searchResults}
              searching={state.searching}
              runSearch={state.runSearch}
              onClose={state.closeSearch}
              onPlay={playItem}
            />

            {status?.connected && (
              <SpotifyBrowsePanel
                tab={state.tab}
                setTab={state.setTab}
                tabItems={state.tabItems}
                tabLoading={state.tabLoading}
                tabSwipeBind={state.tabSwipeBind}
                onPlay={playItem}
                current={current}
                urlEditing={state.urlEditing}
                setUrlEditing={state.setUrlEditing}
                url={state.url}
                setUrl={state.setUrl}
                savingUrl={state.savingUrl}
                saveUrl={state.saveUrl}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
